<script lang="ts" setup>
import { ref, watch, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import NameNsDescription from '@shell/components/form/NameNsDescription.vue';
import LabeledInput from '@components/Form/LabeledInput/LabeledInput.vue';
import KeyValue from '@shell/components/form/KeyValue.vue';
import ArrayList from '@shell/components/form/ArrayList.vue';
import Loading from '@shell/components/Loading.vue';
import Banner from '@components/Banner/Banner.vue';
import ChartValues from '../settings/ChartValues.vue';
import { _EDIT } from '@shell/config/query-params';

import { sortBy } from '@shell/utils/sort';
import { validateKubernetesName } from '@shell/utils/validators/kubernetes-name';
import { EPINIO_TYPES, EpinioNamespace, EpinioAppInfo } from '../../types';
import Application from '../../models/applications';
import { objValuesToString } from '../../utils/settings';

/*interface Data {
  errors: string[],
  values?: EpinioAppInfo
}*/

const store = useStore();

const t = store.getters['i18n/t'];

// Props
const props = defineProps<{
  application: Application;
  mode: string;
  source?: any;
}>();

// Emit function
const emit = defineEmits<{
  (event: 'valid', valid: boolean): void;
  (event: 'change', data: any): void;
}>();

// Reactive state
const errors = ref<string[]>([]); // eslint-disable-line @typescript-eslint/no-unused-vars
const values = ref<EpinioAppInfo | undefined>(undefined);
const validSettings = ref<{ [key: string]: boolean }>({});
const showEnvValues = ref(false);


// Computed properties
const namespaces = computed(() => {
  return sortBy(store.getters['epinio/all'](EPINIO_TYPES.NAMESPACE), 'name', false);
});

const namespaceNames = computed(() => namespaces.value.map((n: EpinioNamespace) => n.meta?.name));

const valid = computed(() => {
  if (!values.value) {
    return false;
  }
  const validName = !!values.value.meta?.name;

  // Namespace must be selected (not empty) and pass naming validation
  const namespaceValue = values.value.meta?.namespace || '';
  const hasNamespace = !!namespaceValue;
  const nsErrors = validateKubernetesName(
    namespaceValue,
    '',
    store.getters,
    undefined,
    [],
  );
  const validNamespace = hasNamespace && nsErrors.length === 0;
  const validInstances = typeof Number(values.value.configuration?.instances) !== 'string' &&
    values.value.configuration?.instances >= 0;

  return validName && validNamespace && validInstances &&
    Object.values(validSettings.value).every((v) => !!v);
});

const toggleEnvVisibility = () => {
  showEnvValues.value = !showEnvValues.value;
};

const eyeIcon = new URL('../../assets/icons/eye-duotone-solid.svg', import.meta.url).href;
const eyeOffIcon = new URL('../../assets/icons/eye-slash-duotone-solid.svg', import.meta.url).href;

const showApplicationVariables = computed(() => {
  return Object.keys(values.value?.configuration?.settings || {}).length !== 0;
});

const isEdit = computed(() => props.mode === _EDIT);

// Generate a default name for new applications
const generateDefaultName = () => {
  try {
    // Use source prop if available (create mode), otherwise try appSource (edit mode)
    const source = props.source || props.application.appSource;
    if (!source) {
      return '';
    }

    let baseName = '';

    // Determine base name from source
    if (source.git?.repo?.name) {
      baseName = source.git.repo.name;
    } else if (source.gitUrl?.url) {
      // Extract base name from git URL
      const urlParts = source.gitUrl.url.split('/');
      baseName = urlParts[urlParts.length - 1].replace(/\.git$/, '');
    } else if (source.container?.url) {
      // Extract base name from container URL
      const urlParts = source.container.url.split('/');
      const imageWithTag = urlParts[urlParts.length - 1];
      baseName = imageWithTag.split(':')[0];
    }

    // Append random string to the end of the base name
    if (baseName) {
      const randomSuffix = Math.random().toString(36).substring(2, 8);
      return `${baseName}-${randomSuffix}`.toLowerCase();
    }

    return '';
  } catch (e) {
    console.log(e);
    return '';
  }
};

// Mounted lifecycle hook
onMounted(() => {
  const defaultName = props.application.meta?.name || (props.mode !== _EDIT ? generateDefaultName() : '');

  // In create mode, don't auto-select the first namespace - require explicit selection
  const defaultNamespace = props.mode === _EDIT
    ? (props.application.meta?.namespace || '')
    : (props.application.meta?.namespace || '');

  const valuesData: EpinioAppInfo = {
    meta: {
      name: defaultName,
      namespace: defaultNamespace
    },
    chart: moveBooleansToFront(props.application.chart?.settings) || {},
    configuration: {
      configurations: props.application.configuration?.configurations || [],
      instances: props.application.configuration?.instances || 1,
      environment: props.application.configuration?.environment || {},
      settings: props.application.configuration?.settings || {},
      routes: props.application.configuration?.routes || [],
    },
  };

  values.value = valuesData;
  validSettings.value = {};

  emit('valid', valid.value);

  populateOnEdit();
});

// Methods
const update = () => {
  emit('change', {
    meta: values.value?.meta,
    configuration: {
      ...values.value?.configuration,
      settings: objValuesToString(values.value?.configuration.settings)
    },
  });
};

// Watchers
watch(() => values.value?.configuration.instances, (newVal) => {
  values.value.configuration.instances = Number(newVal);
  update()
});
watch(() => values.value?.configuration.environment, update);
watch(() => values.value?.configuration.settings, update, { deep: true });
watch(() => values.value?.configuration.routes, update);
watch(valid, (newValid) => {
  emit('valid', newValid);
});

// Handler for name and namespace updates
function handleNameNsUpdate(updatedValue: { metadata?: { name?: string; namespace?: string } }) {
  if (updatedValue?.metadata && values.value?.meta) {
    if (updatedValue.metadata.name !== undefined) {
      values.value.meta.name = updatedValue.metadata.name;
    }
    if (updatedValue.metadata.namespace !== undefined) {
      values.value.meta.namespace = updatedValue.metadata.namespace;
    }
  }
  update();
}

const populateOnEdit = async () => {
  // We need to fetch the chart settings on edit mode.
  if (isEdit.value || props.mode === 'view') {
    const chartList = await store.dispatch(
      'epinio/findAll',
      { type: EPINIO_TYPES.APP_CHARTS },
    );

    const filterChart = chartList?.find(
      (chart: any) => chart.id === props.application.configuration.appchart
    );

    if (filterChart?.settings) {
      const customValues = Object.keys(filterChart?.settings).reduce((acc: any, key: any) => {
        acc[key] = props.application.configuration.settings[key] || '';
        return acc;
      }, {});

      values.value.configuration.settings = customValues;
      values.value.chart = moveBooleansToFront(filterChart.settings);
    }
  }
};

// Allows us to move the checkbox at the top of the list so layout-wise looks better
const moveBooleansToFront = (settingsObj: any) => {
  if (!settingsObj) {
    return;
  }
  const entries = Object.entries(settingsObj);

  entries.sort((a: any, b: any) => {
    const aValue = a[1].type === 'bool' ? 0 : 1;
    const bValue = b[1].type === 'bool' ? 0 : 1;

    return aValue - bValue;
  });

  return Object.fromEntries(entries);
};
</script>

<template>
  <Loading v-if="!values" />
  <div v-else>
    <div class="col">
      <NameNsDescription
        data-testid="epinio_app-info_name-ns"
        :namespaces-override="namespaceNames"
        :create-namespace-override="true"
        :description-hidden="true"
        :value="{metadata: values.meta}"
        :mode="props.mode"
        @update:value="handleNameNsUpdate"
        @createNamespace="ns => values.meta.namespace = ns"
      />
    </div>
    <div class="col span-6">
      <LabeledInput
        v-model:value="values.configuration.instances"
        data-testid="epinio_app-info_instances"
        type="number"
        min="0"
        required
        :mode="props.mode"
        :label="t('epinio.applications.create.instances')"
      />
    </div>
    <div class="spacer" />
    <div class="col span-8">
      <ArrayList
        v-model:value="values.configuration.routes"
        data-testid="epinio_app-info_routes"
        :title="t('epinio.applications.create.routes.title')"
        :protip="t('epinio.applications.create.routes.tooltip')"
        :mode="props.mode"
        :value-placeholder="t('epinio.applications.create.routes.placeholder')"
      />
    </div>
    <div class="spacer" />
    <div v-if="isEdit" class="col span-8">
      <Banner color="info">
        {{ t('epinio.applications.create.settingsVars.description') }}
      </Banner>
    </div>
    <div v-if="showApplicationVariables" class="col span-6">
      <ChartValues
        v-model:value="values.configuration.settings"
        :chart="values.chart"
        :title="t('epinio.applications.create.settingsVars.title')"
        :mode="props.mode"
        :disabled="false"
        @valid="validSettings = $event"
      />
      <div class="spacer" />
    </div>
    <div class="col span-8">
      <div class="env-var-section">
        <div class="env-var-title-row">
          <h3>{{ t('epinio.applications.create.envvar.title') }}</h3>
          <button
            v-if="props.mode === 'edit'"
            class="icon-button"
            type="button"
            :title="showEnvValues ? 'Hide environment variable values' : 'Show environment variable values'"
            :aria-label="showEnvValues ? 'Hide environment variable values' : 'Show environment variable values'"
            @click="toggleEnvVisibility"
          >
            <img v-if="!showEnvValues" :src="eyeIcon" alt="Show values" class="icon" />
            <img v-else :src="eyeOffIcon" alt="Hide values" class="icon" />
          </button>
        </div>
        <KeyValue
          v-model:value="values.configuration.environment"
          :value-concealed="props.mode === 'view' || !showEnvValues"
          data-testid="epinio_app-info_envs"
          :mode="props.mode"
          :key-label="t('epinio.applications.create.envvar.keyLabel')"
          :value-label="t('epinio.applications.create.envvar.valueLabel')"
          :parse-lines-from-file="true"
        />
      </div>
      <div class="mb-20" /> <!-- allow a small amount of padding at bottom -->
    </div>
  </div>
</template>

<style scoped>
  .env-var-section {
    width: 100%;
  }

  .env-var-title-row {
    display: flex;
    align-items: center;
    width: 100%;
    margin-bottom: 10px;
  }

  .env-var-title-row h3 {
    margin: 0;
    margin-right: 8px;
  }

  .icon-button {
    background: none;
    border: none;
    cursor: pointer;
    padding: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .icon-button .icon {
    width: 25px;
    height: 25px;
  }
</style>
